import { HD_BODYGRAPH_CHANNELS } from './hd-bodygraph-metadata';
import { HdBodygraphPlanetaryActivation } from './hd-bodygraph.models';
import { hdGatesCountingForChannel, hdPreShiftGates } from '../hd-global-shift';
import {
  HD_CYCLE_PLANET_GROUPS,
  HdCycleMainKeynoteKey,
  HdCyclePlanetGroupKey,
} from './hd-cycles-cheat-sheet-metadata';
import {
  HD_CHIRON_DISPLAY_CONFIG,
  HdPlanetKey,
  getHdPlanetDisplayConfigByPlanetId,
} from './planet-grid/hd-planet-display-config';

/**
 * Indeling van de cycle-activaties van een cycle chart in de drie
 * cheat-sheet-keynotes, t.o.v. de natal chart (spec: Steven 2026-08-10):
 *
 * - **Persoonlijk** — de cycle vormt een kanaal sámen met de natal chart:
 *   `electromagnetic` (natal één poort, cycle de andere) of `compromise`
 *   (cycle het hele kanaal én natal één van de twee poorten).
 * - **Educatief** — de cycle brengt het hele kanaal op zichzelf
 *   (composite-term: dominantie); natal heeft geen van beide poorten.
 * - **Omgevend** — losse cycle-poortactivaties die in geen enkel (in de
 *   combinatie natal+cycle) gevormd kanaal vallen.
 *
 * Elke poort met cycle-activaties draagt daarnaast een planeet-groep-keynote
 * (Richting/Mensen/Ontwikkeling — niet voor zon/aarde) en een
 * Tegenkoming/Vernieuwing-keynote (Vernieuwing ⇔ de natal chart heeft de poort
 * zelf al). Kanalen die al volledig in de natal chart gedefinieerd zijn dragen
 * géén cyclusthema (Ra kent er geen; composite-term: companionship) maar
 * verschijnen — wanneer de cyclus het héle kanaal opnieuw brengt — wél apart
 * als `renewals`, zodat de student ziet waar die activaties thuishoren
 * (gedempte Vernieuwing-sectie, Steven 2026-10-03); raakt de cyclus er maar
 * één poort van, dan is dat een losse poort-vernieuwing onder Omgevend.
 * Chiron doet nooit mee (hoort niet bij de chart).
 */

/** 'companionship' komt alleen uit de composite-builder, 'renewal' alleen uit de cycle-builder. */
export type HdCycleChannelVariant = 'electromagnetic' | 'compromise' | 'dominance' | 'companionship' | 'renewal';
export type HdCycleEncounterKind = 'encounter' | 'renewal';
/** Wie een poort in een Persoonlijk/Educatief-kanaal activeert. */
export type HdCycleGateSource = 'natal' | 'cycle' | 'both';

/** Eén planetaire cycle-activatie op een poort, met de bijbehorende groep-keynote. */
export interface HdCycleGateActivationVm {
  readonly planetKey: HdPlanetKey;
  readonly glyph: string;
  readonly line: number;
  readonly side: 'personality' | 'design';
  /** Richting/Mensen/Ontwikkeling; `null` voor zon en aarde. */
  readonly planetGroup: HdCyclePlanetGroupKey | null;
}

/** Eén poort-kant van een kanaal-item. */
export interface HdCycleChannelGateVm {
  readonly gate: number;
  readonly source: HdCycleGateSource;
  /** Cycle-activaties op deze poort (leeg voor de natal-kant van een electromagnetic). */
  readonly cycleActivations: readonly HdCycleGateActivationVm[];
  /** Natal-activaties op deze poort (glyph-weergave op de natal-chip). */
  readonly natalActivations: readonly HdCycleGateActivationVm[];
  /** Tegenkoming/Vernieuwing; `null` wanneer de cycle deze poort niet activeert. */
  readonly encounter: HdCycleEncounterKind | null;
}

/** Kanaal dat de cyclus (mee) vormt, voor de secties Persoonlijk en Educatief. */
export interface HdCycleChannelVm {
  readonly channelKey: string;
  readonly variant: HdCycleChannelVariant;
  readonly gates: readonly [HdCycleChannelGateVm, HdCycleChannelGateVm];
}

/** Hangende cycle-poort (sectie Omgevend). */
export interface HdCycleHangingGateVm {
  readonly gate: number;
  readonly cycleActivations: readonly HdCycleGateActivationVm[];
  readonly encounter: HdCycleEncounterKind;
}

export interface HdCycleKeynotesBreakdown {
  readonly personal: readonly HdCycleChannelVm[];
  readonly educational: readonly HdCycleChannelVm[];
  readonly environmental: readonly HdCycleHangingGateVm[];
  /** Natal-volledige kanalen die de cyclus óók volledig brengt: geen thema, enkel kanaal-vernieuwing. */
  readonly renewals: readonly HdCycleChannelVm[];
}

/** planetKey → groep-key (Richting/Mensen/Ontwikkeling); zon/aarde ontbreken bewust. */
const PLANET_GROUP_BY_PLANET: ReadonlyMap<HdPlanetKey, HdCyclePlanetGroupKey> = new Map(
  HD_CYCLE_PLANET_GROUPS.flatMap(group => group.planets.map(planet => [planet, group.key] as const)),
);

export function buildHdCycleKeynotes(
  natalActivations: readonly HdBodygraphPlanetaryActivation[],
  cycleActivations: readonly HdBodygraphPlanetaryActivation[],
): HdCycleKeynotesBreakdown {
  const natalGates = getHdActivatedGates(natalActivations);
  const cycleByGate = hdActivationsByGate(cycleActivations);
  const natalByGate = hdActivationsByGate(natalActivations);

  const personal: HdCycleChannelVm[] = [];
  const educational: HdCycleChannelVm[] = [];
  const renewals: HdCycleChannelVm[] = [];
  /** Poorten die in ≥1 (in de combinatie) gevormd kanaal zitten → nooit Omgevend. */
  const gatesInFormedChannels = new Set<number>();

  // Kanaalbreuk na de shift (19/49): natal-poorten van een post-2027-geboorte
  // tellen niet mee voor dat kanaal; de cycluspoorten (een moment) wel.
  const natalPreShiftGates = hdPreShiftGates(natalActivations.filter(activation => natalGates.has(activation.gate)));

  for (const channel of HD_BODYGRAPH_CHANNELS) {
    const [gateA, gateB] = channel.gates;
    const natalCountingGates = hdGatesCountingForChannel(channel.key, natalGates, natalPreShiftGates);
    const natalHasA = natalCountingGates.has(gateA);
    const natalHasB = natalCountingGates.has(gateB);
    const cycleHasA = cycleByGate.has(gateA);
    const cycleHasB = cycleByGate.has(gateB);

    const formed = (natalHasA || cycleHasA) && (natalHasB || cycleHasB);
    if (!formed) {
      continue;
    }

    const gateVm = (gate: number, natalHas: boolean): HdCycleChannelGateVm => {
      const activations = cycleByGate.get(gate) ?? [];
      return {
        gate,
        source: activations.length > 0 ? (natalHas ? 'both' : 'cycle') : 'natal',
        cycleActivations: activations,
        natalActivations: natalByGate.get(gate) ?? [],
        encounter: activations.length > 0 ? (natalHas ? 'renewal' : 'encounter') : null,
      };
    };
    const vm = (variant: HdCycleChannelVariant): HdCycleChannelVm => ({
      channelKey: channel.key,
      variant,
      gates: [gateVm(gateA, natalHasA), gateVm(gateB, natalHasB)],
    });

    // Al volledig natal-gedefinieerd → geen cyclusthema. Alleen wanneer de
    // cyclus het HELE kanaal opnieuw brengt is het een kanaal-vernieuwing
    // (sectie Vernieuwing); één geraakte poort is gewoon een losse
    // poort-vernieuwing en valt — zoals elke losse poortactivatie — onder
    // Omgevend (met de Vernieuwing-keynote), dus hier bewust niet markeren
    // (Steven 2026-10-03, kanaal 43-23 met enkel transit-43).
    if (natalHasA && natalHasB) {
      if (cycleHasA && cycleHasB) {
        gatesInFormedChannels.add(gateA);
        gatesInFormedChannels.add(gateB);
        renewals.push(vm('renewal'));
      }
      continue;
    }

    gatesInFormedChannels.add(gateA);
    gatesInFormedChannels.add(gateB);

    if (cycleHasA && cycleHasB) {
      // Cycle brengt het hele kanaal: met een natal-poort erbij = compromis
      // (Persoonlijk), zonder = dominantie (Educatief).
      if (natalHasA || natalHasB) {
        personal.push(vm('compromise'));
      } else {
        educational.push(vm('dominance'));
      }
      continue;
    }
    // Cycle activeert precies één kant én het kanaal is gevormd → de andere
    // kant komt uit natal: electromagnetic (Persoonlijk).
    personal.push(vm('electromagnetic'));
  }

  const environmental: HdCycleHangingGateVm[] = [];
  for (const [gate, activations] of cycleByGate) {
    if (gatesInFormedChannels.has(gate)) {
      continue;
    }
    environmental.push({
      gate,
      cycleActivations: activations,
      encounter: natalGates.has(gate) ? 'renewal' : 'encounter',
    });
  }
  environmental.sort((a, b) => a.gate - b.gate);

  return { personal, educational, environmental, renewals };
}

/** Alle geactiveerde poorten (P+D samen); Chiron telt nooit mee. */
export function getHdActivatedGates(
  activations: readonly HdBodygraphPlanetaryActivation[],
): ReadonlySet<number> {
  const gates = new Set<number>();
  for (const activation of activations) {
    if (activation.planetId === HD_CHIRON_DISPLAY_CONFIG.planetId) {
      continue;
    }
    gates.add(activation.gate);
  }
  return gates;
}

/** Eén main-keynote-rol van een cycle-poort, met de kanalen waarin die rol geldt. */
export interface HdCycleGateMainKeynoteEntry {
  readonly keynote: HdCycleMainKeynoteKey;
  /** Kanaal-keys waarin de poort deze rol draagt; leeg voor Omgevend (hangend). */
  readonly channelKeys: readonly string[];
}

/**
 * Main keynotes per cycle-poort, afgeleid uit de breakdown. Een poort kan in
 * meerdere kanalen tegelijk een verschillende rol dragen (bv. Persoonlijk via
 * het ene kanaal én Educatief via het andere) — dan bevat de index beide
 * rollen, in de volgorde Persoonlijk, Educatief, Omgevend, elk met de
 * bijbehorende kanalen. Poorten die enkel een al volledig natal-gedefinieerd
 * kanaal hernieuwen (companionship) ontbreken bewust.
 */
export function buildHdCycleGateMainKeynoteIndex(
  breakdown: HdCycleKeynotesBreakdown,
): ReadonlyMap<number, readonly HdCycleGateMainKeynoteEntry[]> {
  const channelKeysByGateAndKeynote = new Map<number, Map<HdCycleMainKeynoteKey, string[]>>();
  const mark = (gate: number, keynote: HdCycleMainKeynoteKey, channelKey: string | null) => {
    let byKeynote = channelKeysByGateAndKeynote.get(gate);
    if (!byKeynote) {
      byKeynote = new Map();
      channelKeysByGateAndKeynote.set(gate, byKeynote);
    }
    const channelKeys = byKeynote.get(keynote) ?? [];
    if (channelKey != null) {
      channelKeys.push(channelKey);
    }
    byKeynote.set(keynote, channelKeys);
  };
  const markChannels = (channels: readonly HdCycleChannelVm[], keynote: HdCycleMainKeynoteKey) => {
    for (const channel of channels) {
      for (const side of channel.gates) {
        if (side.cycleActivations.length > 0) {
          mark(side.gate, keynote, channel.channelKey);
        }
      }
    }
  };
  markChannels(breakdown.personal, 'personal');
  markChannels(breakdown.educational, 'educational');
  for (const hanging of breakdown.environmental) {
    mark(hanging.gate, 'environmental', null);
  }

  const order: readonly HdCycleMainKeynoteKey[] = ['personal', 'educational', 'environmental'];
  const index = new Map<number, readonly HdCycleGateMainKeynoteEntry[]>();
  for (const [gate, byKeynote] of channelKeysByGateAndKeynote) {
    index.set(gate, order
      .filter(keynote => byKeynote.has(keynote))
      .map(keynote => ({ keynote, channelKeys: byKeynote.get(keynote)! })));
  }
  return index;
}

/** Activaties per poort als weergave-items, in de aangeleverde volgorde. */
export function hdActivationsByGate(
  activations: readonly HdBodygraphPlanetaryActivation[],
): ReadonlyMap<number, readonly HdCycleGateActivationVm[]> {
  const byGate = new Map<number, HdCycleGateActivationVm[]>();
  for (const activation of activations) {
    const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
    if (!config || config.key === HD_CHIRON_DISPLAY_CONFIG.key) {
      continue;
    }
    if (activation.side !== 'personality' && activation.side !== 'design') {
      continue;
    }
    const vm: HdCycleGateActivationVm = {
      planetKey: config.key,
      glyph: config.astronomiconChar,
      line: activation.line,
      side: activation.side,
      planetGroup: PLANET_GROUP_BY_PLANET.get(config.key) ?? null,
    };
    const existing = byGate.get(activation.gate);
    if (existing) {
      existing.push(vm);
    } else {
      byGate.set(activation.gate, [vm]);
    }
  }
  return byGate;
}
